import { Data } from '.generated/Foundation.CW.model';
import { Field, LinkField, useSitecoreContext } from '@sitecore-jss/sitecore-jss-nextjs';
import classNames from 'classnames';
import { useAppointmentModal } from 'helpers/Context/AppointmentModalProvider';
import { withStandardComponentWrapper } from 'helpers/HOC';
import ButtonWrapper from 'helpers/SitecoreWrappers/ButtonWrapper/ButtonWrapper';
import ImageWrapper from 'helpers/SitecoreWrappers/ImageWrapper/ImageWrapper';
import LinkWrapper from 'helpers/SitecoreWrappers/LinkWrapper/LinkWrapper';
import PlainTextWrapper from 'helpers/SitecoreWrappers/PlainTextWrapper/PlainTextWrapper';
import { SvgIcon } from 'helpers/SvgIcon';
import { ComponentProps } from 'lib/component-props';
import useDictionary from 'lib/hooks/useDictionary';
import useIsEditing from 'lib/hooks/useIsEditing';
import { getCurrentDay, getLocationStatus, normalizeTimeFormat } from 'lib/utils/time';
import { formatInTimeZone } from 'date-fns-tz';
import { addDays } from 'date-fns';
import React from 'react';
import { defaultConfig, tv } from 'tailwind-variants';

defaultConfig.twMerge = false;

type LocationHeaderProps = ComponentProps;
type LocationHeaderFieldsProps = Data.BaseTemplateSections.Location & {
  fields?: {
    state?: Data.Lookups.Lookup;
    PageType?: Data.BaseFields.CustomIconImage;
    locations?: {
      fields?: {
        Title?: Field<string>;
        PageType?: {
          fields?: {
            Value?: Field<string>;
          };
        };
      };
      url?: string;
    }[];
    schedulingType?: Data.Lookups.SchedulingTypeLookup;
  };
};
type ContextProps = {
  mapServiceUrl?: LinkField;
};

export type OfficeHoursItem = {
  fields?: {
    officeHourType?: Data.Lookups.OfficeHoursType;
    open?: Field<string>;
    close?: Field<string>;
  };
  name?: string;
};

export const getAddress = ({
  address,
  city,
  state,
  zip,
}: {
  address: string;
  city: string;
  state: string;
  zip: string;
}) => {
  const updatedaddress = address !== '' ? address?.trim() : undefined;
  const updatedcity = city !== '' ? ', ' + city?.trim() : undefined;
  const updatedstate = state !== '' ? ', ' + state?.trim() : undefined;
  const updatedzip = zip !== '' ? zip?.trim() : undefined;
  return `${updatedaddress ?? ''}${updatedcity ?? ''}${updatedstate ?? ''} ${updatedzip ?? ''}`;
};

const LocationHeader = (props: LocationHeaderProps) => {
  const { RenderingIdentifier } = props?.params || {};
  const { getDictionaryValue } = useDictionary();
  const { sitecoreContext } = useSitecoreContext();
  const { route, siteSettings } = sitecoreContext || {};
  const rawFields = (route as LocationHeaderFieldsProps)?.fields ?? {};
  const setting = (siteSettings as ContextProps) ?? {};
  const { base, containerWrapper, heading, locationDirectionsButton } = TAILWIND_VARIANTS();

  const { open } = useAppointmentModal();
  const isEditing = useIsEditing();

  const hoursRegionHeadingId = `${RenderingIdentifier || 'location'}-hours-status-heading`;

  const FullAddress = getAddress({
    address: rawFields?.address1?.value ?? '',
    city: rawFields?.city?.value ?? '',
    state: rawFields?.state?.fields?.name?.value ?? '',
    zip: rawFields?.zip?.value ?? '',
  });

  const MapLocationUrl: string =
    rawFields?.addressMapURL?.value?.href === '' && FullAddress !== ''
      ? `${setting?.mapServiceUrl?.value?.href}${encodeURIComponent(FullAddress)}`
      : '';

  const getDirectionLink = rawFields?.addressMapURL?.value?.href
    ? {
        value: {
          ...rawFields?.addressMapURL?.value,
          target: '_blank',
        },
      }
    : {
        value: {
          href: MapLocationUrl,
          title: getDictionaryValue('GetDirections'),
          text: getDictionaryValue('GetDirections'),
          target: '_blank',
        },
      };
  const schedulingType = rawFields?.schedulingType?.fields?.name?.value
    ?.replaceAll(' ', '')
    ?.toLowerCase();

  const queryString = `?locationName=${rawFields?.locationName?.value}`;
  const officeHours = rawFields?.officeHours as OfficeHoursItem[];
  const cstDayShort = getCurrentDay('America/Chicago');
  const currentDay = officeHours?.find(
    (hour) => hour.fields?.officeHourType?.fields?.dayofWeek?.value === cstDayShort
  );

  const { officeHoursSectionId } = useAppointmentModal();

  const locationStatus = getLocationStatus(
    currentDay?.fields?.open?.value ?? '',
    currentDay?.fields?.close?.value ?? '',
    officeHours ?? []
  );

  if (!(route as LocationHeaderFieldsProps)?.fields || !siteSettings) {
    return null;
  }

  return (
    <section
      id={RenderingIdentifier}
      className={base()}
      data-component="authorable/shared/content/LocationHeader"
    >
      <div className={containerWrapper()}>
        <span className="inline-block mb-12 text-gray-500 text-16 leading-22 font-bold">
          <PlainTextWrapper field={rawFields?.PageType?.fields?.Value} />
        </span>
        <h1 className={heading()}>
          <PlainTextWrapper field={rawFields?.locationName} />
        </h1>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-16 lg:gap-x-[64px] gap-y-16">
          <div className="col-span-1 rounded-md overflow-hidden">
            <ImageWrapper
              field={rawFields?.photo}
              className="w-full h-full object-cover object-center aspect-[3/2]"
            />
          </div>
          <div className="col-span-1 flex flex-col gap-8 md:gap-16">
            {locationStatus && (
              <section
                role="region"
                aria-labelledby={hoursRegionHeadingId}
                className="flex flex-wrap gap-4"
              >
                <h3 id={hoursRegionHeadingId} className="sr-only">
                  {getDictionaryValue('Hours') || 'Hours'}
                </h3>
                <SvgIcon
                  icon={locationStatus?.iconType}
                  size="lg"
                  viewBox="0 0 24 24"
                  className={
                    locationStatus?.iconColor === 'green'
                      ? 'text-utility-green-700'
                      : 'text-utility-red-700'
                  }
                />
                <span className="text-18 group">
                  {locationStatus?.message}
                  <time
                    dateTime={`${formatInTimeZone(
                      locationStatus?.isOpen
                        ? new Date()
                        : locationStatus?.message.includes('at')
                          ? new Date()
                          : addDays(new Date(), 1),
                      'America/Chicago',
                      'yyyy-MM-dd'
                    )}T${normalizeTimeFormat(locationStatus?.isOpen ? (currentDay?.fields?.close?.value ?? '') : (currentDay?.fields?.open?.value ?? ''))}:00-05:00`}
                  >
                    {locationStatus?.time}
                  </time>
                </span>
                {officeHoursSectionId?.uid && (
                  <LinkWrapper
                    field={{
                      value: {
                        linktype: 'internal',
                        anchor: `accordion-${officeHoursSectionId?.uid}`,
                        title: getDictionaryValue('SeeAllHours'),
                        text: getDictionaryValue('SeeAllHours'),
                      },
                    }}
                    isHashLink={true}
                    onClick={() => {
                      if (officeHoursSectionId?.toggleOpen) {
                        officeHoursSectionId.toggleOpen(true);
                      }
                    }}
                  >
                    <span className="text-16 text-blue-700 underline underline-offset-[4px]">
                      {getDictionaryValue('SeeAllHours')}
                    </span>
                  </LinkWrapper>
                )}
              </section>
            )}

            <div className="flex gap-4">
              {FullAddress.trim() !== '' && (
                <span className="inline-block text-orange-700">
                  <SvgIcon icon={'map-pin'} size="lg" />
                </span>
              )}

              {isEditing ? (
                <div className="text-18 group">
                  <div className="relative mb-5 flex flex-wrap gap-4">
                    <PlainTextWrapper field={rawFields?.address1} tag="span" />
                    <PlainTextWrapper field={rawFields?.city} tag="span" />
                    <PlainTextWrapper field={rawFields?.state?.fields?.name} tag="span" />
                    <PlainTextWrapper field={rawFields?.zip} tag="span" />
                  </div>
                  <div className="inline-flex group-hover:underline underline-offset-[6px]">
                    <LinkWrapper field={rawFields?.addressMapURL} ctaVariant="tertiary" />
                  </div>
                </div>
              ) : (
                <LinkWrapper
                  field={{
                    value: {
                      href: getDirectionLink?.value?.href,
                    },
                  }}
                  aria-label={`${getDictionaryValue('GetDirections')} to ${FullAddress}`}
                >
                  <span className="text-18 group block">
                    <span className="relative block group-hover:underline underline-offset-[6px]">
                      <PlainTextWrapper
                        field={{
                          value: FullAddress,
                        }}
                      />
                    </span>
                    <span
                      className={classNames(
                        'inline-flex mt-4 md:mt-0 min-h-[48px] min-w-[48px] group-hover:underline underline-offset-[6px] text-gray-900',
                        locationDirectionsButton()
                      )}
                    >
                      <PlainTextWrapper
                        field={{
                          value: getDirectionLink?.value?.text,
                        }}
                      />
                      <SvgIcon
                        icon="external-link"
                        className="!h-[22px] ml-4 !w-[22px] [&>path]:text-gray-900"
                        size="lg"
                        aria-label={`${getDictionaryValue('GetDirections')}`}
                      />
                    </span>
                  </span>
                </LinkWrapper>
              )}
            </div>
            <div className="flex flex-col md:flex-row md:flex-wrap gap-8 md:gap-12">
              {schedulingType?.toLocaleLowerCase() === 'primarycare' &&
              rawFields?.epicDeptID?.value ? (
                <ButtonWrapper
                  text={rawFields?.schedulingType?.fields?.ctaLabelText?.value} // todo: when we work on appoinment model
                  ctaVariant="primary"
                  className="min-w-[281px]"
                  onClick={() =>
                    open(
                      route?.itemId,
                      queryString,
                      rawFields?.mobileVisiblePhoneNumber?.value || ''
                    )
                  }
                />
              ) : (
                <>
                  {rawFields?.schedulingType?.fields?.cta?.value?.href && (
                    <LinkWrapper
                      field={{
                        value: {
                          href:
                            schedulingType === 'urgentcare' || schedulingType === 'mentalhealth'
                              ? rawFields?.schedulingType?.fields?.cta?.value?.href
                              : `${rawFields?.schedulingType?.fields?.cta?.value?.href}${queryString}`,
                          text: rawFields?.schedulingType?.fields?.ctaLabelText?.value,
                          title: rawFields?.schedulingType?.fields?.ctaLabelText?.value,
                          target: rawFields?.schedulingType?.fields?.cta?.value?.target,
                        },
                      }}
                      suppressNewTabIcon={true}
                      ctaVariant="primary"
                      className="min-w-[281px]"
                    />
                  )}
                </>
              )}

              {!isEditing ? (
                rawFields?.mobileVisiblePhoneNumber?.value !== '' && (
                  <LinkWrapper
                    field={{
                      value: {
                        href: `tel:${rawFields?.mobileVisiblePhoneNumber?.value?.replaceAll('-', '').trim()}`,
                        text: `${getDictionaryValue('Call')} ${rawFields?.mobileVisiblePhoneNumber?.value}`,
                      },
                    }}
                    ctaVariant="secondary"
                    className="min-w-[281px]"
                  />
                )
              ) : (
                <div className="min-w-[281px] flex-row bg-theme-secondary-btn border-theme-secondary-btn-border border-2 flex font-bold font-centuryGothic gap-x-2 items-center justify-center px-12 py-7 !text-18 !leading-25 rounded-sm text-theme-secondary-btn-text text-center">
                  <span className="text-18 leading-25">
                    <PlainTextWrapper
                      field={{ value: getDictionaryValue('Call') }}
                      tag="span"
                      className="text-18 leading-25"
                    />
                  </span>
                  <PlainTextWrapper field={rawFields?.mobileVisiblePhoneNumber} tag="span" />
                </div>
              )}
            </div>
            {rawFields?.locations && rawFields?.locations?.length > 0 && (
              <div>
                <h5 className="font-bold font-centuryGothic text-18 leading-25">
                  <PlainTextWrapper
                    field={{ value: getDictionaryValue('RelatedCareLocationsLabel') }}
                  />
                </h5>
                {rawFields?.locations && (
                  <ul className="flex flex-wrap gap-x-4">
                    {rawFields?.locations?.map((location, index) => {
                      const updatedLink = {
                        value: {
                          text: location?.fields?.PageType?.fields?.Value?.value,
                          title: location?.fields?.PageType?.fields?.Value?.value,
                          href: location?.url,
                        },
                      };
                      return (
                        <li
                          key={`location-type-${index}`}
                          className="inline-flex items-center pr-4 relative before:absolute before:top-1/2 before:right-0 before:-translate-y-1/2 before:h-[25px] before:w-[1px] before:bg-blue-100 before:last:hidden"
                        >
                          <LinkWrapper
                            field={updatedLink}
                            ctaVariant="tertiary"
                            className="!text-blue-700"
                          />
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export const Default = withStandardComponentWrapper(LocationHeader, false);

const TAILWIND_VARIANTS = tv({
  slots: {
    base: ['bg-blue-50', 'theme-lightblue'],
    containerWrapper: ['max-w-screen-xxl', 'mx-auto', 'p-10', 'lg:px-20', 'lg:py-[64px]'],
    heading: [
      'font-bold',
      'font-centuryGothic',
      'mb-16',
      'text-36',
      'leading-43',
      'lg:text-48',
      'lg:leading-58',
      'text-gray-900',
    ],
    description: ['mb-6', 'text-base'],
    locationDirectionsButton: [
      'min-h-[48px]',
      'min-w-[48px]',
      'flex',
      'items-center',
      'justify-center',
      'rounded-md',
      'focus:outline-2',
      'focus:outline-blue-base',
      'transition',
      'duration-150',
    ],
  },
});
